import Linked from 'shared-components/service/Linked'
import { publicConfig } from 'config'
import { newDiscussionHref } from 'lib/community/discussions'
import type { DiscussionCard as Card } from 'lib/community/discussions-types'
import DiscussionCard from 'components/community/discussions/DiscussionCard'
import base from 'components/community/community.module.css'
import SectionHeader from './SectionHeader'
import styles from './ReaderQuestions.module.css'

export const READER_QUESTIONS_ID = 'sec-questions'

/** DealHunt answered this product's list and it is empty — never said on a failed read
 *  (Constraint 84465965). */
export const READER_QUESTIONS_EMPTY = 'Nobody has asked the owners yet.'
/** The empty state's ask; the module's own button otherwise. */
export const READER_QUESTIONS_ASK_FIRST = 'Ask the first question →'

export interface ReaderQuestionsData {
  /** DealHunt's discussions linking this product (≤ 3, `productQuestions`). May be empty: the
   *  module always renders on a product page, so the ask is always there (2026-10-09); the
   *  page's "Questions" nav entry still waits for a card. */
  items: Card[]
  /** DealHunt echoed this product's list (`list.product === node id`) — only then may an empty
   *  list say nobody has asked. false = no list (DealHunt down, older, or another product's). */
  read: boolean
  /** The SoundGuys product slug — "Ask owners" opens the writer with it pre-linked. */
  productSlug: string
  /** The server's timestamp, so ages print the same on the server and the client. */
  now: number
}

/**
 * "Questions from readers" (contract §7): after the Owners section, up to three questions or
 * discussions that link this product — the community's cards, without gear chips or photos (the
 * product is the page) — and "Ask owners" → the writer with the product pre-linked. Shared-cached
 * like the rest of the product page: every figure is DealHunt's public one, and the ask is public
 * content, so the HTML is the same for every visitor.
 *
 * No cards: an empty list DealHunt answered says "Nobody has asked the owners yet." with "Ask the
 * first question →"; a failed read keeps only the heading and "Ask owners".
 */
const ReaderQuestions: React.VFC<ReaderQuestionsData & { productName: string }> = ({
  items,
  read,
  productSlug,
  now,
  productName,
}) => {
  const trailingSlash = publicConfig('useTrailingSlash')
  const askHref = newDiscussionHref({ kind: 'question', product: productSlug }, trailingSlash)
  const empty = items.length === 0 && read
  return (
    <section className={styles.section} aria-labelledby="reader-questions-heading">
      <div id="reader-questions-heading">
        <SectionHeader
          title="Questions from readers"
          intro={`What readers ask about the ${productName}, answered by the people who own it.`}
        />
      </div>
      {items.length > 0 && (
        <div className={styles.list}>
          {items.map(card => (
            <DiscussionCard key={card.id} card={card} now={now} compact headingLevel="h3" />
          ))}
        </div>
      )}
      {empty && <p className={styles.empty}>{READER_QUESTIONS_EMPTY}</p>}
      <Linked href={askHref} className={`${base.btn} ${styles.ask}`}>
        {empty ? READER_QUESTIONS_ASK_FIRST : 'Ask owners'}
      </Linked>
    </section>
  )
}

export default ReaderQuestions
